import type { ExpenseCategory, MemberType, GroupMemberResponseDto } from "@tabipla/shared";

/**
 * Draft state for expense form
 */
export interface ExpenseFormDraft {
  amount: string;
  description: string;
  category: ExpenseCategory;
  date: string;
  splitEqually: boolean;
  selectedMemberKeys: string[];
  customAmounts: Record<string, string>;
}

/**
 * Create a composite key for member lookup (memberType:memberId)
 */
export function memberKey(memberId: string, memberType: MemberType): string {
  return `${memberType}:${memberId}`;
}

/**
 * Parse member key back to memberId and memberType
 */
export function parseMemberKey(key: string): { memberId: string; memberType: MemberType } {
  const [memberType, memberId] = key.split(":") as [MemberType, string];
  return { memberId, memberType };
}

/**
 * Create initial draft with default values
 */
export function getInitialDraft(): ExpenseFormDraft {
  return {
    amount: "",
    description: "",
    category: "other",
    date: new Date().toISOString().split("T")[0],
    splitEqually: true,
    selectedMemberKeys: [],
    customAmounts: {},
  };
}

/**
 * Calculate equal split with remainder distribution
 * The remainder is distributed to the first N members (1 yen each)
 */
export function calculateEqualSplit(
  totalAmount: number,
  memberCount: number
): { baseAmount: number; remainder: number } {
  if (memberCount <= 0) {
    return { baseAmount: 0, remainder: 0 };
  }
  const baseAmount = Math.floor(totalAmount / memberCount);
  const remainder = totalAmount - baseAmount * memberCount;
  return { baseAmount, remainder };
}

/**
 * Calculate share amount for a member in equal split
 * @param index - The index of the member (0-based)
 * @param baseAmount - The base amount per member
 * @param remainder - The remainder to distribute
 */
export function getShareAmount(index: number, baseAmount: number, remainder: number): number {
  return baseAmount + (index < remainder ? 1 : 0);
}

/**
 * Calculate total of custom amounts for selected members
 */
export function calculateTotalCustomAmount(
  customAmounts: Record<string, string>,
  selectedMemberKeys: Set<string>
): number {
  return Object.entries(customAmounts)
    .filter(([key]) => selectedMemberKeys.has(key))
    .reduce((sum, [, value]) => sum + (parseInt(value, 10) || 0), 0);
}

/**
 * Validate expense form
 */
export function isValidExpenseForm(
  amount: string,
  selectedMemberCount: number,
  splitEqually: boolean,
  totalCustomAmount: number
): boolean {
  const parsedAmount = parseInt(amount, 10);
  if (isNaN(parsedAmount) || parsedAmount <= 0) {
    return false;
  }
  if (selectedMemberCount <= 0) {
    return false;
  }
  if (!splitEqually && totalCustomAmount !== parsedAmount) {
    return false;
  }
  return true;
}

/**
 * Check if draft has user-entered content (not just defaults)
 */
export function draftHasUserContent(draft: ExpenseFormDraft): boolean {
  return draft.amount.trim() !== "" || draft.description.trim() !== "";
}

/**
 * Expense categories list
 */
export const EXPENSE_CATEGORIES: ExpenseCategory[] = [
  "food",
  "transport",
  "accommodation",
  "activity",
  "shopping",
  "other",
];

/**
 * Build member map for quick lookup
 */
export function buildMemberMap(
  members: GroupMemberResponseDto[]
): Map<string, GroupMemberResponseDto> {
  const map = new Map<string, GroupMemberResponseDto>();
  members.forEach((m) => map.set(memberKey(m.memberId, m.memberType), m));
  return map;
}
